import React, { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import ReactPlayer from 'react-player';
import { ClickAwayListener, Popper, Tooltip } from '@mui/material';
import { ChromePicker } from 'react-color';
import { useSnackbar } from 'material-ui-snackbar-provider';
import DrawIcon from '@mui/icons-material/DrawOutlined';
import SelectIcon from '@mui/icons-material/MouseOutlined';
import EraserIcon from '@mui/icons-material/AutoFixNormalOutlined';
import SquareIcon from '@mui/icons-material/CropSquareRounded';
import CircleIcon from '@mui/icons-material/RadioButtonUncheckedRounded';
import BeamIcon from '@mui/icons-material/HighlightOutlined';
import AngleIcon from '@mui/icons-material/ArchitectureOutlined';
import LineIcon from '@mui/icons-material/RemoveRounded';
import ArrowIcon from '@mui/icons-material/NorthEastRounded';
import TextIcon from '@mui/icons-material/TextFieldsRounded';
import DeleteIcon from '@mui/icons-material/DeleteOutlineRounded';
import UndoIcon from '@mui/icons-material/UndoRounded';
import ClearIcon from '@mui/icons-material/DeleteSweepOutlined';
import CloseIcon from '@mui/icons-material/CloseRounded';
import PinIcon from '@mui/icons-material/PushPinOutlined';
import SaveIcon from '@mui/icons-material/SaveOutlined';
import CommentIcon from '@mui/icons-material/ChatBubbleOutlineRounded';
import ThicknessIcon from '@mui/icons-material/LineWeightOutlined';
import OpacityIcon from '@mui/icons-material/OpacityOutlined';
import FillIcon from '@mui/icons-material/FormatColorFillOutlined';
import DashedIcon from '@mui/icons-material/MoreHorizRounded';
import FocusIcon from '@mui/icons-material/CenterFocusStrongOutlined';
import WrapIcon from '@mui/icons-material/WrapTextRounded';
import { createDrawingMarker } from '../../api';
import { updateDrawingMarker } from '../../api/commentsApi';
import { useAppDispatch } from '../../store/hooks';
import { setFetchComments } from '../../store/composers/composersSlice';
import { COMMENT } from '../../enums';
import { useDrawingMarkers } from './context';
import { DrawingClip, Point, Shape, ShapeTool } from './types';
import styles from './Drawing.module.scss';
import { angleLabelGeometry, angleSweep, arrowGeometry, cornerResizeScale, curveControlPoint, curvedArrowGeometry, quadraticPoint } from './geometry';

type Tool = ShapeTool | 'select' | 'eraser';
type DrawingSnapshot = { shapes: Shape[]; selectedId: string | null };
type ToolbarDock = 'floating' | 'top' | 'bottom' | 'left' | 'right';
type ToolbarLayout = { dock: ToolbarDock; floatingDock: Exclude<ToolbarDock, 'floating'>; focusMode: boolean; floatingPosition?: Point };
type ToolbarArea = { left: number; top: number; right: number; bottom: number };
type Gesture = { before: DrawingSnapshot; pointerId: number; mode: 'draw' | 'move' | 'resize' | 'erase'; start: Point; shape?: Shape; handle?: number; erasedIds?: Set<string> };
const tooltipContainer = () => document.fullscreenElement || document.body;
const drawingTooltipDefaults = { placement: 'top' as const, enterDelay: 600, enterNextDelay: 600, disableInteractive: true,
  PopperProps: { container: tooltipContainer } };
const OvalIcon = () => <svg viewBox="0 0 24 24" aria-hidden="true"><ellipse cx="12" cy="12" rx="9" ry="5.5" fill="none" stroke="currentColor" strokeWidth="1.8" /></svg>;
const TrapezoidIcon = () => <svg viewBox="0 0 24 24" aria-hidden="true"><path d="M7 5h10l5 14H2z" fill="none" stroke="currentColor" strokeWidth="1.8" /></svg>;
const tools: { tool: Tool; label: string; Icon: React.ElementType }[] = [
  { tool: 'select', label: 'Select / move drawing', Icon: SelectIcon },
  { tool: 'pen', label: 'Freehand', Icon: DrawIcon },
  { tool: 'eraser', label: 'Eraser', Icon: EraserIcon },
  { tool: 'square', label: 'Square / rectangle', Icon: SquareIcon },
  { tool: 'trapezoid', label: 'Trapezoid', Icon: TrapezoidIcon },
  { tool: 'circle', label: 'Circle', Icon: CircleIcon },
  { tool: 'oval', label: 'Oval', Icon: OvalIcon },
  { tool: 'beam', label: 'Light beam', Icon: BeamIcon },
  { tool: 'angle', label: 'Angle indicator: click start, vertex, end', Icon: AngleIcon },
  { tool: 'line', label: 'Line', Icon: LineIcon },
  { tool: 'arrow', label: 'Arrow', Icon: ArrowIcon },
  { tool: 'text', label: 'Text', Icon: TextIcon },
];
const shapeTools = tools.filter(({ tool }) => ['square', 'trapezoid', 'circle', 'oval', 'line', 'arrow', 'angle'].includes(tool));
const toolCaptions: Record<Tool, string> = { select: 'Select', pen: 'Freehand', eraser: 'Eraser', square: 'Rectangle', trapezoid: 'Trap.', circle: 'Circle', oval: 'Oval', beam: 'Light beam', angle: 'Angle', line: 'Line', arrow: 'Arrow', text: 'Text' };
const toolHints: Partial<Record<Tool, string>> = {
  select: 'Select a drawing to move or resize it', eraser: 'Drag over drawings to erase them',
  trapezoid: 'Drag to create a rectangle, then drag any corner independently',
  angle: 'Click the start and vertex, then sweep around the vertex and click to finish (0–360°)', text: 'Add a centered text box; double-click text to edit it',
};
let textMeasure: CanvasRenderingContext2D | null = null;
const textLayout = (shape: Shape) => {
  const size = shape.fontSize || 24 + shape.width * 2;
  const width = Math.max(1, shape.boxWidth || Math.min(1000 - shape.points[0].x, Math.max(80, shape.text.length * size * 0.6)));
  textMeasure ||= document.createElement('canvas').getContext('2d');
  if (textMeasure) textMeasure.font = `${size}px ${shape.fontFamily || 'Arial'}`;
  const lines = shape.text.split('\n').flatMap((line) => {
    if (shape.wrap === false || !shape.boxWidth) return [line];
    const measure = (text: string) => textMeasure?.measureText(text).width ?? text.length * size * 0.6;
    const parts: string[] = [];
    let rest = line;
    while (rest.length > 1 && measure(rest) > width - 12) {
      let max = 1;
      while (max < rest.length && measure(rest.slice(0, max + 1)) <= width - 12) max++;
      const space = rest.lastIndexOf(' ', max);
      const end = space > 0 ? space : max;
      parts.push(rest.slice(0, end)); rest = rest.slice(end + (space > 0 ? 1 : 0));
    }
    return [...parts, rest];
  });
  const contentHeight = Math.max(1, lines.length) * size * 1.2 + 8;
  return { size, width, lines, contentHeight, height: Math.max(contentHeight, shape.boxHeight || 0) };
};
const bounds = (shape: Shape) => {
  if (shape.tool === 'text') {
    const layout = textLayout(shape);
    return { x: shape.points[0].x, y: shape.points[0].y - layout.size,
      width: layout.width, height: layout.height };
  }
  const points = [...shape.points];
  if ((shape.tool === 'line' || shape.tool === 'arrow') && shape.curvePoint && points[1]) {
    const [start, end] = points;
    const control = curveControlPoint(start, end, shape.curvePoint);
    for (const axis of ['x', 'y'] as const) {
      const t = (start[axis] - control[axis]) / (start[axis] - 2 * control[axis] + end[axis]);
      if (t > 0 && t < 1) points.push(quadraticPoint(start, control, end, t));
    }
  }
  const xs = points.map((point) => point.x);
  const ys = points.map((point) => point.y);
  const x = Math.min(...xs);
  const y = Math.min(...ys);
  return { x, y, width: Math.max(...xs) - x || 1, height: Math.max(...ys) - y || 1 };
};
const corners = (shape: Shape) => {
  const { x, y, width, height } = bounds(shape);
  return [{ x, y }, { x: x + width, y }, { x: x + width, y: y + height }, { x, y: y + height }];
};
const resizeHandles = (shape: Shape) => {
  if ((shape.tool === 'line' || shape.tool === 'arrow') && shape.points[1]) {
    const [start, end] = shape.points;
    return [...shape.points, shape.curvePoint || { x: (start.x + end.x) / 2, y: (start.y + end.y) / 2 }];
  }
  if (shape.tool === 'angle' || shape.tool === 'trapezoid') return shape.points;
  const points = corners(shape);
  const middleY = (points[0].y + points[3].y) / 2;
  const middleX = (points[0].x + points[1].x) / 2;
  return shape.tool === 'text' ? [...points, { x: points[0].x, y: middleY }, { x: points[1].x, y: middleY },
    { x: middleX, y: points[0].y }, { x: middleX, y: points[3].y }] : points;
};
const path = (points: Point[]) => points.map((point, index) => `${index ? 'L' : 'M'}${point.x},${point.y}`).join(' ');
const circleEnd = (start: Point, end: Point, height: number): Point => {
  const dx = end.x - start.x, dy = end.y - start.y;
  const size = Math.min(Math.max(Math.abs(dx), Math.abs(dy)), dx < 0 ? start.x : 1000 - start.x,
    dy < 0 ? start.y : height - start.y);
  return { x: start.x + (dx < 0 ? -size : size), y: start.y + (dy < 0 ? -size : size) };
};

const DrawingShape = ({ shape }: { shape: Shape }) => {
  const { x, y, width, height } = bounds(shape);
  const [a, b, c] = shape.points;
  let body: React.ReactNode;
  if (shape.tool === 'pen' || shape.tool === 'line' || shape.tool === 'angle' || shape.tool === 'arrow') {
    const control = (shape.tool === 'line' || shape.tool === 'arrow') && shape.curvePoint && b
      ? curveControlPoint(a, b, shape.curvePoint) : null;
    const d = control ? `M${a.x},${a.y} Q${control.x},${control.y} ${b.x},${b.y}` : path(shape.points);
    body = <>
      <path d={d} fill="none" stroke="transparent" strokeWidth={Math.max(18, shape.width)} pointerEvents="stroke" />
      {shape.tool === 'arrow' && b ? (() => {
        const arrow = control ? curvedArrowGeometry(a, control, b, shape.width) : arrowGeometry(a, b, shape.width);
        if (!arrow) return null;
        return <>{arrow.shaftVisible && <path d={arrow.shaftPath} fill="none" />}
          <polygon fill={shape.color} stroke="none" points={[arrow.tip, arrow.left, arrow.right].map((point) => `${point.x},${point.y}`).join(' ')} /></>;
      })() : shape.points.length === 1 ? <circle cx={a.x} cy={a.y} r={shape.width / 2} fill={shape.color} /> : <path d={d} fill="none" />}
      {shape.tool === 'angle' && c && (() => {
        const label = angleLabelGeometry(a, b, c, shape.width, 24, shape.reflexAngle);
        return label && <text x={label.position.x} y={label.position.y} textAnchor="middle" dominantBaseline="central"
          fill={shape.color} stroke="none" fontSize={24}>{Math.round(label.degrees)}°</text>;
      })()}
    </>;
  } else if (shape.tool === 'square') {
    body = <rect x={x} y={y} width={width} height={height} pointerEvents="all" />;
  } else if (shape.tool === 'trapezoid') {
    body = <polygon points={shape.points.map(({ x, y }) => `${x},${y}`).join(' ')} pointerEvents="all" />;
  } else if (shape.tool === 'circle' || shape.tool === 'oval') {
    body = <ellipse cx={x + width / 2} cy={y + height / 2} rx={width / 2} ry={height / 2} pointerEvents="all" />;
  } else if (shape.tool === 'beam') {
    body = <>
      <defs><linearGradient id={`drawing-beam-${shape.id}`} x1="0" y1="0" x2="0" y2="1">
        <stop offset="0%" stopColor={shape.color} stopOpacity="0.8" /><stop offset="100%" stopColor={shape.color} stopOpacity="0.1" />
      </linearGradient></defs>
      <polygon points={`${x + width * 0.35},${y} ${x + width * 0.65},${y} ${x + width},${y + height} ${x},${y + height}`} fill={`url(#drawing-beam-${shape.id})`} stroke="none" />
    </>;
  } else {
    const layout = textLayout(shape);
    const alignment = shape.textAlign || 'left';
    const textX = alignment === 'center' ? x + width / 2 : alignment === 'right' ? x + width - 6 : x + (shape.boxWidth ? 6 : 0);
    body = <>
      <rect x={x} y={y} width={width} height={height} fill={shape.fill ? 'rgba(80,80,80,.55)' : 'transparent'}
        stroke="rgba(255,255,255,.3)" strokeWidth={1} strokeDasharray="none" vectorEffect="non-scaling-stroke" pointerEvents="all" />
      <text x={textX} y={a.y} fill={shape.color} stroke="none" fontFamily={shape.fontFamily || 'Arial'} fontSize={layout.size}
        textAnchor={alignment === 'center' ? 'middle' : alignment === 'right' ? 'end' : 'start'} xmlSpace="preserve">
        {layout.lines.map((line, index) => <tspan key={index} x={textX} dy={index ? layout.size * 1.2 : 0}>{line || '\u00a0'}</tspan>)}
      </text>
    </>;
  }
  const maskId = `drawing-eraser-${shape.id}`;
  return <>
    {!!shape.erasures?.length && <defs><mask id={maskId} maskUnits="userSpaceOnUse" x="-100%" y="-100%" width="300%" height="300%">
      <rect x="-100%" y="-100%" width="300%" height="300%" fill="#fff" />
      {shape.erasures.map((erasure, index) => {
        const points = erasure.points.map((point) => ({ x: point.x / erasure.rx, y: point.y / erasure.ry }));
        return <g key={index} transform={`translate(${x} ${y}) scale(${width * erasure.rx} ${height * erasure.ry})`}
          fill="#000" stroke="#000" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round">
          {points.length === 1 ? <circle cx={points[0].x} cy={points[0].y} r={1} /> : <path d={path(points)} fill="none" />}
        </g>;
      })}
    </mask></defs>}
    <g data-drawing-id={shape.id} pointerEvents="auto" mask={shape.erasures?.length ? `url(#${maskId})` : undefined} stroke={shape.color} strokeWidth={shape.width} opacity={shape.opacity}
    strokeLinecap="round" strokeLinejoin="round" strokeDasharray={shape.dashed ? '12 8' : undefined}
    fill={shape.fill ? shape.color : 'none'}>{body}</g></>;
};

export const VideoDrawing = ({ playing, pause, playerRef, sourceKey, feedId, videoId, videoIds, seconds, autoStop, selectedClip, playbackClip = selectedClip, hasSelectedClips, showVideoDrawingsWhenPlayingClips = false, reservePlayerControls = false, jumpToMarker }: {
  playing: boolean; pause: () => void; playerRef: React.RefObject<ReactPlayer>; sourceKey: string;
  feedId: string; videoId: string; videoIds: string[]; seconds: number; autoStop: boolean; jumpToMarker: (comment: COMMENT, automatic?: boolean) => boolean;
  selectedClip?: DrawingClip; playbackClip?: DrawingClip; hasSelectedClips: boolean; showVideoDrawingsWhenPlayingClips?: boolean; reservePlayerControls?: boolean;
}) => {
  const launcherBottomSpace = reservePlayerControls ? 86 : 38;
  const { markers, markersLoaded, request, showMarker, dismissRequest, refresh, openDiscussion, toggleFocusDiscussion, setDrawingFocused, setDrawingMinHeight, seekSequence } = useDrawingMarkers();
  const notification = useSnackbar();
  const dispatch = useAppDispatch();
  const [active, setActive] = useState(false);
  const [toolbarVisible, setToolbarVisible] = useState(true);
  const [toolbarTall, setToolbarTall] = useState(false);
  const [tool, setTool] = useState<Tool>('pen');
  const [eraserPosition, setEraserPosition] = useState<Point | null>(null);
  const [shapes, setShapes] = useState<Shape[]>([]);
  const [undoHistory, setUndoHistory] = useState<DrawingSnapshot[]>([]);
  const styleStart = useRef<DrawingSnapshot | null>(null);
  const [draft, setDraft] = useState<Shape | null>(null);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [color, setColor] = useState('#35b9ee');
  const [colorPickerOpen, setColorPickerOpen] = useState(false);
  const [fontAnchor, setFontAnchor] = useState<HTMLButtonElement | null>(null);
  const colorButtonRef = useRef<HTMLButtonElement>(null);
  const colorWindowRef = useRef<HTMLDivElement>(null);
  const colorSelectionPointer = useRef<{ id?: number; start: Point; dragged: boolean } | null>(null);
  useEffect(() => {
    if (!colorPickerOpen) return;
    const closeOnEscape = (event: KeyboardEvent) => {
      if (event.key === 'Escape') { event.stopImmediatePropagation(); setColorPickerOpen(false); }
    };
    const closeOnOutside = (event: PointerEvent) => {
      const target = event.target as Node;
      if (!colorWindowRef.current?.contains(target) && !colorButtonRef.current?.contains(target)) setColorPickerOpen(false);
    };
    window.addEventListener('keydown', closeOnEscape, true);
    document.addEventListener('pointerdown', closeOnOutside, true);
    return () => {
      window.removeEventListener('keydown', closeOnEscape, true);
      document.removeEventListener('pointerdown', closeOnOutside, true);
    };
  }, [colorPickerOpen]);
  const [width, setWidth] = useState(4);
  const [eraserWidth, setEraserWidth] = useState(9);
  const [opacity, setOpacity] = useState(1);
  const [fill, setFill] = useState(false);
  const [dashed, setDashed] = useState(false);
  const [editingTextId, setEditingTextId] = useState<string | null>(null);
  const [fontFamily, setFontFamily] = useState('Arial');
  const [fontSize, setFontSize] = useState(32);
  const [textAlign, setTextAlign] = useState<'left' | 'center' | 'right'>('left');
  const [wrap, setWrap] = useState(true);
  const textEditStart = useRef<DrawingSnapshot | null>(null);
  const textEditorRef = useRef<HTMLTextAreaElement>(null);
  const [saving, setSaving] = useState(false);
  const [drawingAspect, setDrawingAspect] = useState<number | null>(null);
  const [savedMarker, setSavedMarker] = useState<COMMENT | null>(null);
  const [savedMarkerLoaded, setSavedMarkerLoaded] = useState(false);
  const [drawingTarget, setDrawingTarget] = useState<{ timestamp: number; videoId: string; clipId?: string; label: string } | null>(null);
  const [frame, setFrame] = useState({ x: 0, y: 0, width: 0, height: 0, aspect: 16 / 9 });
  const [dockAnchor, setDockAnchor] = useState<HTMLButtonElement | null>(null);
  const [toolbarLayout, setToolbarLayout] = useState<ToolbarLayout>(() => {
    try {
      const cached = JSON.parse(localStorage.getItem('drawing-toolbar-layout') || 'null');
      const edges = ['top', 'bottom', 'left', 'right'];
      if (cached && [...edges, 'floating'].includes(cached.dock) && edges.includes(cached.floatingDock) && typeof cached.focusMode === 'boolean') {
        return { dock: cached.dock, floatingDock: cached.floatingDock, focusMode: false,
          floatingPosition: Number.isFinite(cached.floatingPosition?.x) && Number.isFinite(cached.floatingPosition?.y) ? cached.floatingPosition : undefined };
      }
    } catch { /* Storage can be unavailable. */ }
    return { dock: 'right', floatingDock: 'right', focusMode: false };
  });
  const { dock: toolbarDock, floatingDock, focusMode, floatingPosition } = toolbarLayout;
  const toolbarVertical = ['left', 'right'].includes(toolbarDock === 'floating' ? floatingDock : toolbarDock);
  const [focusShades, setFocusShades] = useState<Pick<DOMRect, 'left' | 'top' | 'width' | 'height'>[]>([]);
  const [launcherPosition, setLauncherPosition] = useState<{ side: 'left' | 'right'; y: number } | null>(() => {
    try {
      const cached = JSON.parse(localStorage.getItem('drawing-launcher-position') || 'null');
      return cached && ['left', 'right'].includes(cached.side) && Number.isFinite(cached.y)
        ? { side: cached.side, y: Math.max(0, Math.min(1, cached.y)) } : null;
    } catch { return null; }
  });
  const launcherDrag = useRef<{ pointerId: number; start: Point } | null>(null);
  const launcherMoved = useRef(false);
  const [toolbarPosition, setToolbarPosition] = useState<Point | null>(null);
  const toolbarDrag = useRef<{ pointerId: number; start: Point; origin: Point; position: Point } | null>(null);
  const [toolbarContainer, setToolbarContainer] = useState<Element>(tooltipContainer);
  const rootRef = useRef<HTMLDivElement>(null);
  const toolbarRef = useRef<HTMLDivElement>(null);
  const toolbarArea = useRef<ToolbarArea | null>(null);
  const drawingTooltipProps = { ...drawingTooltipDefaults,
    placement: (toolbarVertical ? toolbarDock === 'right' ? 'left' : 'right' : 'top') as 'top' | 'left' | 'right',
    PopperProps: { container: tooltipContainer, popperOptions: { strategy: 'fixed' as const }, modifiers: [{ name: 'offset', options: {
      offset: ({ placement, reference }: { placement: string; reference: { x: number; y: number; width: number; height: number } }) => {
        if (focused) return [0, 4];
        const box = toolbarRef.current?.getBoundingClientRect();
        const gap = !box ? 0 : placement.startsWith('top') ? reference.y - box.top
          : placement.startsWith('bottom') ? box.bottom - reference.y - reference.height
          : placement.startsWith('left') ? reference.x - box.left : box.right - reference.x - reference.width;
        return [0, Math.max(0, gap)];
      },
    } }] },
  };
  const gesture = useRef<Gesture | null>(null);
  const canvasRef = useRef<SVGSVGElement>(null);
  const penDraftFrame = useRef<number | null>(null);
  const cancelPenDraft = () => {
    if (penDraftFrame.current !== null) window.cancelAnimationFrame(penDraftFrame.current);
    penDraftFrame.current = null;
  };
  const anglePoints = useRef<Point[]>([]);
  const angleSweepState = useRef<{ direction: number; angle?: number }>({ direction: 0 });
  const pointerDownShape = useRef<{ id: string | null; shapes: Shape[]; undo: DrawingSnapshot[] } | null>(null);
  const clickedShape = useRef<{ id: string | null; shapes: Shape[]; undo: DrawingSnapshot[] } | null>(null);
  const nextId = useRef(0);
  const handledRequest = useRef(0);
  const lastStoppedTimestamp = useRef<number | null>(null);
  const lastStoppedContext = useRef('');
  const markerContext = (comment: COMMENT) => `${feedId}:${comment.clip_id != null ? `clip:${comment.clip_id}` : 'video'}`;
  const playbackContext = `${feedId}:${playbackClip?.id || 'video'}`;
  const previousPlayback = useRef({ seconds, videoId, playing: false, context: playbackContext });
  const previousWorkspace = useRef({ seconds, sourceKey, feedId, playing, clipId: selectedClip?.id, playbackId: playbackClip?.id });
  const pausedPlaybackTime = useRef<number | null>(null);
  const pausedMarker = useRef<{ timestamp: number; clipId?: string } | null>(null);
  const selected = shapes.find((shape) => shape.id === selectedId);
  const currentWidth = tool === 'eraser' ? eraserWidth : selected?.width ?? width;
  const textOptionsDisabled = selected ? selected.tool !== 'text' : tool !== 'text';
  const styleTool = tool === 'eraser' ? tool : selected?.tool || tool;
  const supportsFill = ['text', 'square', 'trapezoid', 'circle', 'oval'].includes(styleTool);
  const supportsDashed = !['text', 'beam', 'eraser', 'select'].includes(styleTool);
  useEffect(() => { if (textOptionsDisabled) setFontAnchor(null); }, [textOptionsDisabled]);
  useEffect(() => { lastStoppedTimestamp.current = null; pausedMarker.current = null; }, [seekSequence]);
  const videoIdsKey = videoIds.join(',');
  const viewHeight = 1000 / (drawingAspect || frame.aspect);
  const canEditDrawing = !savedMarker || (savedMarkerLoaded && savedMarker.can_edit_drawing === true &&
    markers.find((marker) => String(marker.id) === String(savedMarker.id))?.can_edit_drawing === true);

  const focused = focusMode && active && toolbarVisible;
  useEffect(() => {
    setDrawingFocused(focused);
    return () => setDrawingFocused(false);
  }, [focused, setDrawingFocused]);
  useEffect(() => {
    try { localStorage.setItem('drawing-toolbar-layout', JSON.stringify(toolbarLayout)); } catch { /* Storage can be unavailable. */ }
  }, [toolbarLayout]);
  useEffect(() => {
    if (launcherPosition) { try { localStorage.setItem('drawing-launcher-position', JSON.stringify(launcherPosition)); } catch { /* Storage can be unavailable. */ } }
  }, [launcherPosition]);
  useEffect(() => {
    if (editingTextId) { textEditorRef.current?.focus({ preventScroll: true }); textEditorRef.current?.setSelectionRange(0, textEditorRef.current.value.length); }
  }, [editingTextId]);
  useEffect(() => {
    if (!colorPickerOpen) return;
    const finishColorSelection = (event: MouseEvent | PointerEvent) => {
      const selection = colorSelectionPointer.current;
      if (selection && (event.type === 'mouseup' || selection.id === (event as PointerEvent).pointerId)) {
        colorSelectionPointer.current = null;
        if (event.type !== 'pointercancel' && !selection.dragged && Math.hypot(event.clientX - selection.start.x, event.clientY - selection.start.y) <= 4) setColorPickerOpen(false);
      }
    };
    const moveColorSelection = (event: MouseEvent | PointerEvent) => {
      const selection = colorSelectionPointer.current;
      if (selection && (event.type === 'mousemove' || selection.id === (event as PointerEvent).pointerId)
        && Math.hypot(event.clientX - selection.start.x, event.clientY - selection.start.y) > 4) selection.dragged = true;
    };
    window.addEventListener('mousemove', moveColorSelection);
    window.addEventListener('mouseup', finishColorSelection);
    window.addEventListener('pointermove', moveColorSelection);
    window.addEventListener('pointerup', finishColorSelection);
    window.addEventListener('pointercancel', finishColorSelection);
    return () => {
      colorSelectionPointer.current = null;
      window.removeEventListener('mousemove', moveColorSelection);
      window.removeEventListener('mouseup', finishColorSelection);
      window.removeEventListener('pointermove', moveColorSelection);
      window.removeEventListener('pointerup', finishColorSelection);
      window.removeEventListener('pointercancel', finishColorSelection);
    };
  }, [colorPickerOpen]);
  useLayoutEffect(() => {
    const root = rootRef.current;
    if (!focused || !root) return;
    let animationFrame: number | null = null;
    const update = () => {
      const rect = root.getBoundingClientRect();
      const left = Math.max(0, Math.min(window.innerWidth, rect.left));
      const right = Math.max(left, Math.min(window.innerWidth, rect.right));
      const top = Math.max(0, Math.min(window.innerHeight, rect.top));
      const bottom = Math.max(top, Math.min(window.innerHeight, rect.bottom));
      setFocusShades([
        { left: 0, top: 0, width: window.innerWidth, height: top },
        { left: 0, top: bottom, width: window.innerWidth, height: window.innerHeight - bottom },
        { left: 0, top, width: left, height: bottom - top },
        { left: right, top, width: window.innerWidth - right, height: bottom - top },
      ]);
    };
    const scheduleUpdate = () => {
      if (animationFrame !== null) return;
      animationFrame = window.requestAnimationFrame(() => { animationFrame = null; update(); });
    };
    update();
    scheduleUpdate();
    const observer = new ResizeObserver(scheduleUpdate);
    observer.observe(root);
    window.addEventListener('resize', scheduleUpdate);
    window.addEventListener('scroll', scheduleUpdate, true);
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape' && !anglePoints.current.length) { event.preventDefault(); event.stopPropagation(); setToolbarLayout((layout) => ({ ...layout, focusMode: false })); }
    };
    window.addEventListener('keydown', onKeyDown, true);
    return () => {
      if (animationFrame !== null) window.cancelAnimationFrame(animationFrame);
      observer.disconnect();
      window.removeEventListener('resize', scheduleUpdate);
      window.removeEventListener('scroll', scheduleUpdate, true);
      window.removeEventListener('keydown', onKeyDown, true);
    };
  }, [focused, toolbarContainer, toolbarDock, toolbarVertical, floatingDock]);

  const clampToolbarPosition = (position: Point): Point => {
    const toolbar = toolbarRef.current;
    if (!toolbar) return position;
    const scale = parseFloat(toolbar.style.getPropertyValue('--compact-scale')) || 1;
    const area = toolbarArea.current || { left: 8, top: 8, right: window.innerWidth - 8, bottom: window.innerHeight - 8 };
    return { x: Math.max(area.left, Math.min(area.right - toolbar.offsetWidth * scale, position.x)),
      y: Math.max(area.top, Math.min(area.bottom - toolbar.offsetHeight * scale, position.y)) };
  };

  useEffect(() => {
    const updateContainer = () => setToolbarContainer(tooltipContainer());
    document.addEventListener('fullscreenchange', updateContainer);
    return () => document.removeEventListener('fullscreenchange', updateContainer);
  }, []);
  useLayoutEffect(() => {
    const toolbar = toolbarRef.current;
    if (!active || (playing && !focused) || !toolbar) return;
    const stage = rootRef.current?.closest('[data-drawing-stage]');
    const workspace = stage?.parentElement;
    const edge = toolbarDock === 'floating' ? floatingDock : toolbarDock;
    let measuredWidth = -1;
    let animationFrame: number | null = null;
    const measureToolbarWidth = (availableWidth: number) => {
      if (toolbarVertical || focused || measuredWidth === availableWidth) return;
      measuredWidth = availableWidth;
      toolbar.dataset.sizing = 'true';
      toolbar.style.removeProperty('width');
      toolbar.style.width = `${toolbar.offsetWidth}px`;
      delete toolbar.dataset.sizing;
    };
    const positionToolbar = () => {
      const rect = (workspace || rootRef.current)?.getBoundingClientRect();
      const left = Math.max(8, (rect?.left || 0) + 8);
      const right = Math.min(window.innerWidth - 8, (rect?.right ?? window.innerWidth) - 8);
      const top = Math.max(8, (rect?.top || 0) + 8);
      const bottom = Math.min(window.innerHeight - 8, (rect?.bottom ?? window.innerHeight) - 8);
      setToolbarTall(toolbarDock !== 'floating' && toolbarVertical && !focused && bottom - top >= 600);
      if (focused) {
        setDrawingMinHeight(356);
        const scale = Math.min(1, Math.max(30, bottom - top) / 340);
        const width = `${Math.max(30, right - left) / scale}px`, height = `${Math.max(340, bottom - top)}px`;
        toolbar.style.setProperty('--detailed-zoom', String(scale));
        toolbar.style.width = width; toolbar.style.maxWidth = width;
        toolbar.style.height = height; toolbar.style.maxHeight = height;
        toolbar.dataset.short = String(bottom - top < 215);
        if (workspace) (['top', 'right', 'bottom', 'left'] as const).forEach((side) => {
          const panel = side === 'left' ? styles.drawingTools : side === 'right' ? styles.settings : null;
          const value = `${panel ? ((toolbar.querySelector<HTMLElement>(`.${panel}`)?.offsetWidth || 0) + 14) * scale + 16 : 0}px`;
          if (workspace.style.getPropertyValue(`--drawing-rail-${side}`) !== value) workspace.style.setProperty(`--drawing-rail-${side}`, value);
        });
        toolbarArea.current = { left, top, right, bottom };
        setToolbarPosition((position) => position?.x === left / scale && position.y === top / scale ? position : { x: left / scale, y: top / scale });
        return;
      }
      if (toolbarDock === 'floating') {
        const area = { left: 8, top: 8, right: window.innerWidth - 8, bottom: window.innerHeight - 8 };
        toolbarArea.current = area;
        toolbar.style.maxWidth = `${Math.max(30, area.right - area.left)}px`;
        toolbar.style.maxHeight = `${Math.max(30, area.bottom - area.top)}px`;
        toolbar.style.width = toolbarVertical ? '' : `${Math.min(1100, Math.max(30, area.right - area.left))}px`;
        toolbar.style.removeProperty('height');
        toolbar.dataset.short = String(area.bottom - area.top < (toolbarVertical ? 150 : 84));
        setDrawingMinHeight(0);
        (['top', 'right', 'bottom', 'left'] as const).forEach((side) => workspace?.style.setProperty(`--drawing-rail-${side}`, '0px'));
        const width = toolbar.offsetWidth, height = toolbar.offsetHeight;
        const next = clampToolbarPosition(toolbarDrag.current?.position || floatingPosition || {
          x: edge === 'left' ? (rect?.left || 0) - width - 8 : edge === 'right' ? (rect?.right ?? right) + 8 : left,
          y: edge === 'top' ? (rect?.top || 0) - height - 8 : edge === 'bottom' ? (rect?.bottom ?? bottom) + 8 : top,
        });
        setToolbarPosition((position) => position?.x === next.x && position.y === next.y ? position : next);
        return;
      }
      const maxWidth = `${Math.max(30, (right - left) / (workspace && toolbarVertical ? 2 : 1))}px`;
      const maxHeight = `${Math.max(30, (bottom - top) / (workspace && !toolbarVertical ? 2 : 1))}px`;
      if (toolbar.style.maxWidth !== maxWidth) toolbar.style.maxWidth = maxWidth;
      if (toolbar.style.maxHeight !== maxHeight) toolbar.style.maxHeight = maxHeight;
      const height = toolbarVertical ? maxHeight : '';
      if (toolbar.style.height !== height) toolbar.style.height = height;
      const short = String(parseFloat(maxHeight) < (toolbarVertical ? focused ? 215 : 150 : 84));
      if (toolbar.dataset.short !== short) toolbar.dataset.short = short;
      if (toolbarVertical) {
        toolbar.style.removeProperty('width');
        toolbar.dataset.measuring = 'true';
        toolbar.style.height = 'auto'; toolbar.style.maxHeight = 'none';
        setDrawingMinHeight(Math.ceil(Math.min(toolbar.scrollHeight + 16, toolbarTall ? 600 : Infinity)));
        delete toolbar.dataset.measuring;
        toolbar.style.maxHeight = maxHeight;
        toolbar.style.height = maxHeight;
      }
      measureToolbarWidth(right - left);
      if (!toolbarVertical) setDrawingMinHeight(Math.ceil(toolbar.scrollHeight * 2 + 16));
      const toolbarWidth = toolbar.offsetWidth, toolbarHeight = toolbar.offsetHeight;
      const area = { left, top, right, bottom };
      if (workspace) {
        const space = (toolbarVertical ? toolbarWidth : toolbarHeight) + 16;
        (['top', 'right', 'bottom', 'left'] as const).forEach((side) => {
          const property = `--drawing-rail-${side}`, value = `${side === edge ? space : 0}px`;
          if (workspace.style.getPropertyValue(property) !== value) workspace.style.setProperty(property, value);
        });
        if (edge === 'left') area.right = left + toolbarWidth;
        if (edge === 'right') area.left = right - toolbarWidth;
        if (edge === 'top') area.bottom = top + toolbarHeight;
        if (edge === 'bottom') area.top = bottom - toolbarHeight;
      }
      toolbarArea.current = area;
      setToolbarPosition((position) => {
        const next = clampToolbarPosition({
          x: edge === 'left' ? left : edge === 'right' ? right - toolbarWidth : left + (right - left - toolbarWidth) / 2,
          y: toolbarVertical || edge === 'top' ? top : edge === 'bottom' ? bottom - toolbarHeight : top + (bottom - top - toolbarHeight) / 2,
        });
        return position?.x === next.x && position.y === next.y ? position : next;
      });
    };
    const schedulePosition = () => {
      if (animationFrame !== null) return;
      animationFrame = window.requestAnimationFrame(() => { animationFrame = null; positionToolbar(); });
    };
    positionToolbar();
    const observer = new ResizeObserver(schedulePosition);
    observer.observe(toolbar);
    const observedWorkspace = workspace || rootRef.current;
    if (observedWorkspace) observer.observe(observedWorkspace);
    window.addEventListener('resize', schedulePosition);
    window.addEventListener('scroll', schedulePosition, true);
    return () => {
      if (animationFrame !== null) window.cancelAnimationFrame(animationFrame);
      observer.disconnect(); toolbar.style.removeProperty('width'); toolbar.style.removeProperty('height'); toolbar.style.removeProperty('--detailed-zoom');
      setDrawingMinHeight(0);
      toolbarArea.current = null;
      (['top', 'right', 'bottom', 'left'] as const).forEach((side) => workspace?.style.removeProperty(`--drawing-rail-${side}`));
      window.removeEventListener('resize', schedulePosition); window.removeEventListener('scroll', schedulePosition, true);
    };
  }, [active, playing, toolbarContainer, toolbarVisible, toolbarDock, toolbarVertical, floatingDock, floatingPosition, focused, canEditDrawing, toolbarTall, setDrawingMinHeight]);

  const clear = () => {
    cancelPenDraft();
    pointerDownShape.current = null; clickedShape.current = null; styleStart.current = null; setUndoHistory([]); setDockAnchor(null);
    setShapes([]); setDraft(null); setSelectedId(null); setEraserPosition(null); setColorPickerOpen(false); setDrawingAspect(null);
    setEditingTextId(null); setFontAnchor(null); textEditStart.current = null; gesture.current = null; anglePoints.current = [];
  };
  useEffect(() => { if (playing) { clear(); setActive(focusMode && toolbarVisible); if (!focusMode) setTool('select'); setDrawingTarget(null); setSavedMarker(null); pausedMarker.current = null; dismissRequest(); } }, [playing]);
  useEffect(() => {
    if (!canEditDrawing) {
      setSelectedId(null); setDraft(null); setColorPickerOpen(false); setUndoHistory([]); setEditingTextId(null);
      gesture.current = null; anglePoints.current = [];
    }
  }, [canEditDrawing]);
  const beginDrawing = () => {
    setToolbarVisible(true);
    clear(); pausedMarker.current = null; setSavedMarker(null); setSavedMarkerLoaded(false);
    setDrawingTarget({ timestamp: playerRef.current?.getCurrentTime() ?? seconds, videoId,
      clipId: selectedClip?.id, label: selectedClip?.label || 'Video comments' });
    pause(); setActive(true); setTool('pen');
  };
  useEffect(() => {
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.defaultPrevented || event.isComposing || event.repeat || event.ctrlKey || event.metaKey || event.altKey || event.key !== 'd') return;
      const target = event.target instanceof HTMLElement ? event.target : null;
      if (target?.isContentEditable || target?.closest('input, textarea, select, [contenteditable], [role="textbox"], [role="dialog"]') || document.querySelector('[role="dialog"]')) return;
      event.preventDefault();
      if (active && (!playing || focused)) {
        if (toolbarVisible) closeToolbar(); else setToolbarVisible(true);
      } else beginDrawing();
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [active, playing, focused, toolbarVisible, seconds, videoId, selectedClip?.id]);
  const displayMarker = (comment: COMMENT) => {
    if (!comment.drawing) return;
    clear();
    setSavedMarker(comment);
    setSavedMarkerLoaded(markers.some((marker) => String(marker.id) === String(comment.id)));
    const clipId = comment.clip_id != null ? String(comment.clip_id) : undefined;
    setDrawingTarget({ timestamp: comment.timestamp, videoId: comment.drawing.videoId, clipId,
      label: clipId ? selectedClip?.id === clipId ? selectedClip.label : `Clip ${clipId}` : 'Video comments' });
    setDrawingAspect(comment.drawing.aspect);
    setShapes(comment.drawing.shapes.map((shape) => ({ ...shape, id: String(++nextId.current), points: shape.points.map((point) => ({ ...point })) })));
    setActive(true); if (!focusMode) setTool('select'); pausedMarker.current = { timestamp: comment.timestamp, clipId };
    lastStoppedTimestamp.current = comment.timestamp;
    setToolbarVisible((visible) => focusMode && visible);
    lastStoppedContext.current = markerContext(comment);
  };
  useEffect(() => {
    if (playing || !active || !savedMarker) return;
    const marker = markers.find((item) => String(item.id) === String(savedMarker.id));
    if (marker?.drawing && (!savedMarkerLoaded || (savedMarker.can_edit_drawing !== true && marker.can_edit_drawing === true))) {
      displayMarker(marker); setToolbarVisible(toolbarVisible);
    }
  }, [markers, savedMarker?.id, savedMarker?.can_edit_drawing, savedMarkerLoaded, playing, active]);
  useEffect(() => {
    if (!request?.comment.drawing) return;
    if (handledRequest.current !== request.sequence) {
      handledRequest.current = request.sequence;
      if (!jumpToMarker(request.comment, request.automatic)) {
        dismissRequest(); notification.showMessage('The clip or video angle for this drawing is no longer available');
        return;
      }
    }
    const clipReady = request.comment.clip_id != null
      ? playbackClip?.id === String(request.comment.clip_id) && (request.automatic || selectedClip?.id === String(request.comment.clip_id))
      : request.automatic || !hasSelectedClips;
    if (!playing && clipReady && request.comment.drawing.videoId === videoId && Math.abs(seconds - request.comment.timestamp) < 0.001) {
      displayMarker(request.comment);
      dismissRequest();
    }
  }, [request, videoId, seconds, playing, playbackClip?.id, selectedClip?.id, hasSelectedClips]);
  useEffect(() => {
    const previous = previousWorkspace.current;
    previousWorkspace.current = { seconds, sourceKey, feedId, playing, clipId: selectedClip?.id, playbackId: playbackClip?.id };
    if (playing) pausedPlaybackTime.current = null;
    else if (previous.playing) pausedPlaybackTime.current = playerRef.current?.getCurrentTime() ?? seconds;
    if (request?.comment.drawing || playing || previous.playing) return;
    const changedTime = Math.abs(seconds - previous.seconds) > 0.001;
    const changedClip = selectedClip?.id !== previous.clipId || playbackClip?.id !== previous.playbackId;
    const changedSource = sourceKey !== previous.sourceKey;
    if (!changedTime && !changedClip && !changedSource) return;
    const pauseProgress = !changedSource && !changedClip && pausedPlaybackTime.current != null &&
      Math.abs(seconds - pausedPlaybackTime.current) < 0.1;
    pausedPlaybackTime.current = null;
    if (pauseProgress) return;
    const stopped = pausedMarker.current;
    if (!changedSource && active && !savedMarker && !shapes.length && !draft) {
      setDrawingTarget({ timestamp: seconds, videoId, clipId: selectedClip?.id, label: selectedClip?.label || 'Video comments' });
      pausedMarker.current = null; lastStoppedTimestamp.current = null;
      return;
    }
    clear(); setActive(false); setDrawingTarget(null); setSavedMarker(null);
    if (changedSource && feedId === previous.feedId && !changedClip && stopped && Math.abs(seconds - stopped.timestamp) < 0.25) {
      const marker = markers.find((comment) => comment.drawing?.videoId === videoId &&
        Math.abs(comment.timestamp - stopped.timestamp) < 0.001 &&
        (comment.clip_id != null ? String(comment.clip_id) : undefined) === stopped.clipId);
      if (marker) displayMarker(marker);
      return;
    }
    pausedMarker.current = null;
    lastStoppedTimestamp.current = null;
  }, [seconds, sourceKey, feedId, selectedClip?.id, playbackClip?.id, playing, request]);
  useEffect(() => {
    if (markersLoaded) return;
    const player = playerRef.current?.getInternalPlayer();
    const video = player instanceof HTMLVideoElement ? player : rootRef.current?.parentElement?.querySelector('video');
    if (!video) return;
    const onSeek = () => { previousPlayback.current.seconds = video.currentTime; };
    video.addEventListener('seeking', onSeek);
    return () => video.removeEventListener('seeking', onSeek);
  }, [markersLoaded, playerRef, sourceKey, playing]);
  useEffect(() => {
    if (request?.comment.drawing) {
      previousPlayback.current = { seconds: request.comment.timestamp, videoId: request.comment.drawing.videoId, playing: false,
        context: `${feedId}:${request.comment.clip_id || 'video'}` };
      return;
    }
    const previous = previousPlayback.current;
    // Keep the playback start until the initial marker request finishes.
    if (playing && autoStop && !markersLoaded && playbackContext === previous.context) return;
    previousPlayback.current = { seconds, videoId, playing, context: playbackContext };
    const changedClip = playbackContext !== previous.context;
    if (seconds < previous.seconds - 0.001) {
      lastStoppedTimestamp.current = null;
      if (!changedClip) return;
    }
    if (!playing || !autoStop || !markersLoaded) return;
    const candidates = markers.filter((comment) => comment.drawing && videoIds.includes(comment.drawing.videoId) &&
      (comment.timestamp !== lastStoppedTimestamp.current || markerContext(comment) !== lastStoppedContext.current) &&
      (comment.clip_id != null
        ? !!playbackClip && String(comment.clip_id) === playbackClip.id && comment.timestamp >= playbackClip.time_start && comment.timestamp <= playbackClip.time_end
        : !hasSelectedClips || (showVideoDrawingsWhenPlayingClips && !!playbackClip &&
          comment.timestamp >= playbackClip.time_start && comment.timestamp <= playbackClip.time_end)) && comment.timestamp <= seconds &&
      (comment.timestamp > previous.seconds || (!previous.playing && comment.timestamp === previous.seconds) ||
        (changedClip && comment.timestamp === seconds)));
    const marker = candidates.sort((a, b) => a.timestamp - b.timestamp ||
      Number(b.drawing!.videoId === videoId) - Number(a.drawing!.videoId === videoId) ||
      videoIds.indexOf(a.drawing!.videoId) - videoIds.indexOf(b.drawing!.videoId))[0];
    if (marker) {
      showMarker(marker, true);
      previousPlayback.current = { seconds: marker.timestamp, videoId, playing: false, context: playbackContext };
    }
  }, [seconds, videoId, videoIdsKey, playing, autoStop, markers, markersLoaded, request, playbackContext, hasSelectedClips, showVideoDrawingsWhenPlayingClips]);
  const saveMarker = async () => {
    if (!canEditDrawing || saving || !shapes.length || draft || !feedId || !videoId) return;
    setSaving(true);
    try {
      const currentTime = playerRef.current?.getCurrentTime() ?? seconds;
      if (!drawingTarget || drawingTarget.videoId !== videoId || (!savedMarker && drawingTarget.clipId !== selectedClip?.id) ||
        Math.abs(currentTime - drawingTarget.timestamp) > 0.25 ||
        (!savedMarker && selectedClip && (currentTime < selectedClip.time_start || currentTime > selectedClip.time_end))) {
        notification.showMessage('Playback or clip selection changed. Start a new drawing before saving');
        return;
      }
      const drawing = { version: 1 as const, videoId: drawingTarget.videoId, aspect: drawingAspect || frame.aspect, shapes };
      const { data } = savedMarker
        ? await updateDrawingMarker(savedMarker.id, { drawing })
        : await createDrawingMarker({
          videofeedId: feedId, timestamp: drawingTarget.timestamp, message: '',
          ...(drawingTarget.clipId && { clipId: drawingTarget.clipId }), drawing,
        });
      lastStoppedTimestamp.current = data.timestamp;
      lastStoppedContext.current = `${feedId}:${drawingTarget.clipId ? `clip:${drawingTarget.clipId}` : 'video'}`;
      setSavedMarker({ ...data, clip_id: data.clip_id ?? drawingTarget.clipId, can_edit_drawing: savedMarker?.can_edit_drawing === true });
      setSavedMarkerLoaded(markers.some((marker) => String(marker.id) === String(data.id)));
      if (focused) {
        setUndoHistory([]); setDockAnchor(null); setColorPickerOpen(false); setFontAnchor(null);
      } else closeToolbar();
      setEditingTextId(null);
      refresh(); openDiscussion(data, drawingTarget.clipId, true);
      dispatch(setFetchComments());
      notification.showMessage(savedMarker ? 'Drawing marker updated' : 'Drawing marker saved');
    } catch (error: any) {
      notification.showMessage(error?.response?.data?.message || 'Could not save drawing marker');
    } finally { setSaving(false); }
  };
  useEffect(() => {
    const root = rootRef.current;
    if (!root) return;
    const player = playerRef.current?.getInternalPlayer();
    const video = player instanceof HTMLVideoElement ? player : root.parentElement?.querySelector('video');
    let animationFrame: number | null = null;
    const measure = () => {
      const aspect = video?.videoWidth && video.videoHeight ? video.videoWidth / video.videoHeight : 16 / 9;
      const width = Math.min(root.clientWidth, root.clientHeight * aspect);
      const height = width / aspect;
      const next = { x: (root.clientWidth - width) / 2, y: (root.clientHeight - height) / 2, width, height, aspect };
      setFrame((previous) => previous.x === next.x && previous.y === next.y && previous.width === width
        && previous.height === height && previous.aspect === aspect ? previous : next);
    };
    const scheduleMeasure = () => {
      if (animationFrame !== null) return;
      animationFrame = window.requestAnimationFrame(() => { animationFrame = null; measure(); });
    };
    measure();
    const observer = new ResizeObserver(scheduleMeasure);
    observer.observe(root);
    video?.addEventListener('loadedmetadata', scheduleMeasure);
    return () => {
      if (animationFrame !== null) window.cancelAnimationFrame(animationFrame);
      observer.disconnect(); video?.removeEventListener('loadedmetadata', scheduleMeasure);
    };
  }, [playerRef, sourceKey, active]);

  const rememberUndo = (snapshot: DrawingSnapshot) => {
    setUndoHistory((history) => history.at(-1) === snapshot ? history : [...history, snapshot]);
  };
  const deleteSelected = () => {
    if (!selected || !canEditDrawing || gesture.current) return;
    rememberUndo({ shapes, selectedId });
    setShapes((previous) => previous.filter((shape) => shape.id !== selectedId)); setSelectedId(null);
  };
  useEffect(() => {
    if (!active || playing || !toolbarVisible || !canEditDrawing || (!selected && !anglePoints.current.length)) return;
    const onKeyDown = (event: KeyboardEvent) => {
      const cancelAngle = anglePoints.current.length > 0 && (event.key === 'Escape' || event.key === 'Backspace');
      if (event.defaultPrevented || event.isComposing || event.ctrlKey || event.metaKey || event.altKey ||
        (!cancelAngle && (!selected || (event.key !== 'Delete' && event.key !== 'Backspace')))) return;
      const target = event.target instanceof HTMLElement ? event.target : null;
      if (target?.isContentEditable || target?.closest('input, textarea, select, [contenteditable], [role="textbox"], [role="dialog"]')) return;
      if (document.querySelector('[role="dialog"]')) return;
      event.preventDefault(); event.stopImmediatePropagation();
      if (cancelAngle) {
        anglePoints.current = []; angleSweepState.current = { direction: 0 }; setDraft(null);
      } else deleteSelected();
    };
    window.addEventListener('keydown', onKeyDown, true);
    return () => window.removeEventListener('keydown', onKeyDown, true);
  }, [active, playing, toolbarVisible, canEditDrawing, selected, shapes, draft]);
  const undo = () => {
    const previous = undoHistory.at(-1);
    if (!previous || !canEditDrawing || gesture.current) return;
    setEditingTextId(null); setFontAnchor(null); textEditStart.current = null;
    setShapes(previous.shapes); setSelectedId(previous.selectedId); setUndoHistory((history) => history.slice(0, -1));
    setDraft(null); anglePoints.current = [];
  };
  const changeStyle = (update: Partial<Shape>) => {
    if (!selected) return;
    if (Object.entries(update).every(([key, value]) => selected[key as keyof Shape] === value)) return;
    rememberUndo(styleStart.current || { shapes, selectedId });
    setShapes((previous) => previous.map((shape) => shape.id === selectedId ? { ...shape, ...update } : shape));
  };
  const finishTextEdit = () => {
    const before = textEditStart.current;
    if (before && shapes.some((shape, index) => shape !== before.shapes[index])) rememberUndo(before);
    textEditStart.current = null; setEditingTextId(null);
  };
  const addText = (point: Point) => {
    rememberUndo({ shapes, selectedId });
    const position = { x: Math.min(920, point.x), y: Math.min(viewHeight - fontSize * 0.2 - 8, Math.max(fontSize, point.y)) };
    const shape: Shape = { id: String(++nextId.current), tool: 'text', color, width, opacity, fill, dashed: false, text: '',
      points: [position], boxWidth: Math.min(280, 1000 - position.x), fontFamily, fontSize, textAlign, wrap };
    setShapes((previous) => [...previous, shape]); setSelectedId(shape.id); setTool('select');
    textEditStart.current = null; setEditingTextId(shape.id);
  };
  const pointFromEvent = (event: { clientX: number; clientY: number; currentTarget: EventTarget | null },
    sample: Pick<PointerEvent, 'clientX' | 'clientY'> = event,
    rect = (event.currentTarget as SVGSVGElement).getBoundingClientRect()): Point => {
    return { x: Math.max(0, Math.min(1000, (sample.clientX - rect.left) / rect.width * 1000)),
      y: Math.max(0, Math.min(viewHeight, (sample.clientY - rect.top) / rect.height * viewHeight)) };
  };
  useLayoutEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas || playing || !toolbarVisible || !canEditDrawing) return;
    const movePen = (input: Event) => {
      const event = input as PointerEvent;
      const current = gesture.current;
      if (current?.mode !== 'draw' || current.pointerId !== event.pointerId || current.shape?.tool !== 'pen') return;
      const rect = canvas.getBoundingClientRect();
      const samples = event.getCoalescedEvents?.();
      current.shape = { ...current.shape, points: [...current.shape.points,
        ...(samples?.length ? samples : [event]).map((sample) => pointFromEvent(event, sample, rect))] };
      if (penDraftFrame.current !== null) return;
      penDraftFrame.current = window.requestAnimationFrame(() => {
        penDraftFrame.current = null;
        const latest = gesture.current;
        if (latest?.mode === 'draw' && latest.shape?.tool === 'pen') setDraft(latest.shape);
      });
    };
    // Collect input before React's continuous-event rendering; painting must not throttle sampling.
    const eventName = 'onpointerrawupdate' in window ? 'pointerrawupdate' : 'pointermove';
    canvas.addEventListener(eventName, movePen);
    return () => { canvas.removeEventListener(eventName, movePen); cancelPenDraft(); };
  }, [active, playing, toolbarVisible, canEditDrawing, viewHeight]);
  const erase = (point: Point) => {
    const current = gesture.current!;
    const start = current.start;
    const erasedIds = new Set(current.erasedIds);
    const radius = eraserWidth / 2;
    setShapes((previous) => previous.map((shape) => {
      const box = bounds(shape);
      const node = rootRef.current?.querySelector<SVGGraphicsElement>(`[data-drawing-id="${shape.id}"]`);
      const hitBox = node?.getBBox?.() || box;
      const continuing = erasedIds.has(shape.id);
      if (!continuing && (Math.max(start.x, point.x) + radius < hitBox.x - shape.width / 2
        || Math.min(start.x, point.x) - radius > hitBox.x + hitBox.width + shape.width / 2
        || Math.max(start.y, point.y) + radius < hitBox.y - shape.width / 2
        || Math.min(start.y, point.y) - radius > hitBox.y + hitBox.height + shape.width / 2)) return shape;
      const normalize = (p: Point) => ({ x: (p.x - box.x) / box.width, y: (p.y - box.y) / box.height });
      const erasures = shape.erasures || [];
      const last = erasures.at(-1);
      current.erasedIds!.add(shape.id);
      return { ...shape, erasures: continuing && last
        ? [...erasures.slice(0, -1), { ...last, points: [...last.points, normalize(point)] }]
        : [...erasures, { points: start.x === point.x && start.y === point.y ? [normalize(point)] : [normalize(start), normalize(point)],
          rx: radius / box.width, ry: radius / box.height }] };
    }));
    current.start = point;
  };
  const onPointerDown = (event: React.PointerEvent<SVGSVGElement>) => {
    if (event.button !== 0 || gesture.current) return;
    const target = event.target as Element;
    const id = target.closest('[data-drawing-id]')?.getAttribute('data-drawing-id');
    if (!toolbarVisible) {
      if (!id) return;
      event.preventDefault(); event.stopPropagation();
      pointerDownShape.current = null; clickedShape.current = null;
      setToolbarVisible(true); setTool('select'); setSelectedId(canEditDrawing ? id : null);
      return;
    }
    if (!canEditDrawing) return;
    event.preventDefault(); event.stopPropagation();
    if (!drawingTarget && tool !== 'select' && tool !== 'eraser') {
      setDrawingTarget({ timestamp: playerRef.current?.getCurrentTime() ?? seconds, videoId,
        clipId: selectedClip?.id, label: selectedClip?.label || 'Video comments' });
    }
    if (editingTextId) finishTextEdit();
    const point = pointFromEvent(event);
    if (tool === 'eraser') setEraserPosition(point);
    clickedShape.current = pointerDownShape.current;
    pointerDownShape.current = { id: id || null, shapes, undo: undoHistory };
    const before = { shapes, selectedId };
    if (tool === 'angle') {
      const last = anglePoints.current.at(-1);
      if (last && Math.hypot(point.x - last.x, point.y - last.y) <= 2) return;
      const points = [...anglePoints.current, point];
      if (points.length < 3) angleSweepState.current = { direction: 0 };
      const sweep = points.length === 3
        ? angleSweep(points[0], points[1], points[2], angleSweepState.current.direction, angleSweepState.current.angle) : null;
      const shape: Shape = draft ? { ...draft, points } : {
        id: String(++nextId.current), tool, color, width, opacity, fill, dashed, text: '', points,
      };
      if (points.length === 3) {
        rememberUndo(before);
        setShapes((previous) => [...previous, { ...shape, reflexAngle: sweep?.reflexAngle ?? false }]); setSelectedId(shape.id);
        setDraft(null); anglePoints.current = []; setTool('select');
      } else {
        anglePoints.current = points; setSelectedId(null); setDraft({ ...shape, points: [...points, point] });
      }
      return;
    }
    event.currentTarget.setPointerCapture(event.pointerId);
    if (tool === 'eraser') {
      gesture.current = { before, pointerId: event.pointerId, mode: 'erase', start: point, erasedIds: new Set() };
      erase(point);
    } else if (tool === 'select' || target.hasAttribute('data-handle')) {
      const shape = shapes.find((item) => item.id === id);
      setSelectedId(shape?.id || null);
      if (shape) gesture.current = { before, pointerId: event.pointerId, mode: target.hasAttribute('data-handle') ? 'resize' : 'move',
        handle: Number(target.getAttribute('data-handle')), shape, start: point };
    } else {
      const shape: Shape = { id: String(++nextId.current), tool, color, width, opacity, fill, dashed, text: '',
        points: tool === 'pen' ? [point] : tool === 'trapezoid' ? [point, point, point, point] : [point, point] };
      setSelectedId(null);
      if (tool === 'text') {
        addText(point);
      } else {
        gesture.current = { before, pointerId: event.pointerId, mode: 'draw', start: point, shape };
        setDraft(shape);
      }
    }
  };
  const onPointerMove = (event: React.PointerEvent<SVGSVGElement>) => {
    if (tool === 'eraser') setEraserPosition(pointFromEvent(event));
    if (tool === 'angle' && anglePoints.current.length) {
      const point = pointFromEvent(event);
      const points = [...anglePoints.current, point];
      const sweep = points.length === 3
        ? angleSweep(points[0], points[1], points[2], angleSweepState.current.direction, angleSweepState.current.angle) : null;
      if (sweep) angleSweepState.current = sweep;
      setDraft((previous) => previous && { ...previous, points, reflexAngle: sweep?.reflexAngle ?? false });
      return;
    }
    const current = gesture.current;
    if (!current || current.pointerId !== event.pointerId) return;
    if (current.mode === 'draw' && current.shape?.tool === 'pen') return;
    let point = pointFromEvent(event);
    if ((current.mode === 'move' || current.mode === 'resize') && point.x === current.start.x && point.y === current.start.y &&
      shapes.find((shape) => shape.id === current.shape?.id) === current.shape) return;
    if (current.mode === 'erase') {
      erase(point); return;
    }
    const shape = current.shape!;
    if (current.mode === 'draw') {
      if (shape.tool === 'circle') {
        point = circleEnd(current.start, point, viewHeight);
      }
      const { start } = current;
      const points = shape.tool === 'trapezoid'
        ? [start, { x: point.x, y: start.y }, point, { x: start.x, y: point.y }]
        : [start, point];
      current.shape = { ...shape, points }; setDraft(current.shape);
    } else {
      let points: Point[];
      let curvePoint = shape.curvePoint;
      if (current.mode === 'move') {
        const box = bounds(shape);
        const dx = Math.max(-box.x, Math.min(1000 - box.x - box.width, point.x - current.start.x));
        const dy = Math.max(-box.y, Math.min(viewHeight - box.y - box.height, point.y - current.start.y));
        points = shape.points.map((p) => ({ x: p.x + dx, y: p.y + dy }));
        if (curvePoint) curvePoint = { x: curvePoint.x + dx, y: curvePoint.y + dy };
      } else if (shape.tool === 'text') {
        const box = bounds(shape), layout = textLayout(shape);
        const handle = current.handle!;
        const left = [0, 3, 4].includes(handle), top = handle < 2;
        const anchorX = box.x + (left ? box.width : 0);
        let resized: Shape;
        if (handle >= 6) {
          const top = handle === 6;
          const boxHeight = Math.max(layout.contentHeight, Math.min(top ? box.y + box.height : viewHeight - box.y,
            box.height + (point.y - current.start.y) * (top ? -1 : 1)));
          resized = { ...shape, boxHeight, points: [{ x: shape.points[0].x,
            y: (top ? box.y + box.height - boxHeight : box.y) + layout.size }] };
        } else if (handle >= 4) {
          const boxWidth = Math.max(60, Math.min(left ? anchorX : 1000 - anchorX,
            box.width + (point.x - current.start.x) * (left ? -1 : 1)));
          resized = { ...shape, boxWidth, points: [{ x: anchorX - (left ? boxWidth : 0), y: shape.points[0].y }] };
        } else {
          const anchorY = box.y + (top ? box.height : 0);
          const corner = corners(shape)[handle];
          const scale = cornerResizeScale({ x: anchorX, y: anchorY }, corner,
            { x: corner.x + point.x - current.start.x, y: corner.y + point.y - current.start.y });
          const maxScale = Math.min(96 / layout.size, ((left ? anchorX : 1000 - anchorX) - 12) / Math.max(1, box.width - 12),
            ((top ? anchorY : viewHeight - anchorY) - 8) / (box.height - 8));
          const size = Math.max(16, Math.min(Math.floor(layout.size * maxScale + 0.000001),
            Math.round(layout.size * scale)));
          const boxWidth = (box.width - 12) * size / layout.size + 12;
          resized = { ...shape, fontSize: size, boxWidth, wrap: shape.boxWidth ? shape.wrap : false };
          if (shape.boxHeight !== undefined) resized.boxHeight = (box.height - 8) * size / layout.size + 8;
          resized.points = [{ x: anchorX - (left ? boxWidth : 0),
            y: anchorY - (top ? textLayout(resized).height : 0) + size }];
        }
        setShapes((previous) => previous.map((item) => item.id === shape.id ? resized : item));
        return;
      } else if (['line', 'arrow', 'angle', 'trapezoid'].includes(shape.tool)) {
        points = shape.points.map((p, index) => index === current.handle ? point : p);
        if ((shape.tool === 'line' || shape.tool === 'arrow') && current.handle === 2) curvePoint = point;
      } else {
        const opposite = corners(shape)[(current.handle! + 2) % 4];
        if (shape.tool === 'circle') point = circleEnd(opposite, point, viewHeight);
        const box = bounds(shape);
        const x = Math.min(point.x, opposite.x), y = Math.min(point.y, opposite.y);
        const w = Math.abs(point.x - opposite.x), h = Math.abs(point.y - opposite.y);
        points = shape.points.map((p) => ({ x: x + (p.x - box.x) / box.width * w, y: y + (p.y - box.y) / box.height * h }));
      }
      const previousShape = shapes.find((item) => item.id === shape.id) || shape;
      const previousSweep = shape.tool === 'angle' && current.mode === 'resize'
        ? angleSweep(previousShape.points[0], previousShape.points[1], previousShape.points[2]) : null;
      const direction = (previousSweep?.direction || 1) * (previousShape.reflexAngle ? -1 : 1);
      const sweep = previousSweep ? angleSweep(points[0], points[1], points[2], direction, previousSweep.angle) : null;
      setShapes((previous) => previous.map((item) => item.id === shape.id
        ? { ...item, points, ...(curvePoint && { curvePoint }), ...(sweep && { reflexAngle: sweep.reflexAngle }) } : item));
    }
  };
  const finish = (event: React.PointerEvent<SVGSVGElement>, cancelled = false) => {
    const current = gesture.current;
    if (!current || current.pointerId !== event.pointerId) return;
    cancelPenDraft();
    if (cancelled && current.mode !== 'draw') {
      setShapes(current.before.shapes); setSelectedId(current.before.selectedId);
    } else if (!cancelled && current.mode !== 'draw' && (shapes.length !== current.before.shapes.length ||
      shapes.some((shape, index) => shape !== current.before.shapes[index]))) {
      rememberUndo(current.before);
    }
    if (!cancelled && current.mode === 'draw' && current.shape) {
      let shape = current.shape;
      if (shape.tool === 'pen' && event.type === 'pointerup') {
        const point = pointFromEvent(event), last = shape.points.at(-1)!;
        if (point.x !== last.x || point.y !== last.y) shape = { ...shape, points: [...shape.points, point] };
      }
      if (shape.tool === 'pen' || (shape.tool === 'trapezoid'
        ? bounds(shape).width > 2 && bounds(shape).height > 2 : Math.hypot(shape.points[0].x - shape.points.at(-1)!.x, shape.points[0].y - shape.points.at(-1)!.y) > 2)) {
        rememberUndo(current.before);
        setShapes((previous) => [...previous, shape]);
        if (shape.tool !== 'pen') setSelectedId(shape.id);
      }
    }
    gesture.current = null; setDraft(null);
    if (event.currentTarget.hasPointerCapture(event.pointerId)) event.currentTarget.releasePointerCapture(event.pointerId);
  };
  const closeToolbar = () => {
    setToolbarVisible(false); setUndoHistory([]); setDockAnchor(null); setColorPickerOpen(false); setFontAnchor(null);
    setToolbarLayout((layout) => ({ ...layout, focusMode: false }));
  };
  const chooseTool = (value: Tool) => {
    if (playing || !drawingTarget) {
      if (playing) pause();
      setDrawingTarget({ timestamp: playerRef.current?.getCurrentTime() ?? seconds, videoId,
        clipId: selectedClip?.id, label: selectedClip?.label || 'Video comments' });
    }
    finishTextEdit(); setDraft(null); anglePoints.current = [];
    if (selected?.tool === 'text' && !selected.text.trim()) {
      setShapes((previous) => previous.filter((shape) => shape.id !== selected.id));
    }
    if (value === 'text') addText({ x: 360, y: viewHeight / 2 + fontSize * 0.4 - 4 });
    else {
      const shapeTool = shapeTools.some((item) => item.tool === value);
      if (value === 'eraser' && tool !== 'eraser') setEraserWidth(9);
      setTool(!playing && !shapeTool && tool === value ? 'select' : value); setSelectedId(null);
    }
  };
  const saveAction = (canEditDrawing || focused) && <Tooltip {...drawingTooltipProps} title={!canEditDrawing ? savedMarkerLoaded ? 'This drawing is view only' : 'Loading drawing permissions…' : saving ? 'Saving drawing…' : draft ? 'Finish the current drawing before saving'
    : !videoId ? 'Select a video before saving' : !shapes.length ? 'Add a drawing before saving' : 'Save drawing and add a comment'}>
    <span className={styles.saveAction}><button type="button" className={styles.saveButton}
      aria-label={savedMarker ? 'Save drawing changes' : 'Save drawing marker'}
      disabled={!canEditDrawing || !shapes.length || !!draft || saving || !videoId}
      onClick={saveMarker}><SaveIcon /><span className={styles.saveCaption}>{saving ? 'Saving…' : focused ? 'Save' : savedMarker ? 'Save Changes' : 'Save Comment'}</span></button></span>
  </Tooltip>;
  const iconButton = (label: string, Icon: React.ElementType, onClick: () => void, disabled = false, caption = label.split(' ')[0], hint = label) => <Tooltip {...drawingTooltipProps} title={hint}>
    <span><button type="button" className={styles.expandButton} aria-label={label} disabled={disabled} onClick={onClick}><Icon /><span className={styles.buttonCaption}>{caption}</span></button></span>
  </Tooltip>;
  const finishLauncherDrag = (event: React.PointerEvent<HTMLButtonElement>) => {
    if (launcherDrag.current?.pointerId !== event.pointerId) return;
    launcherDrag.current = null;
    if (event.currentTarget.hasPointerCapture(event.pointerId)) event.currentTarget.releasePointerCapture(event.pointerId);
  };
  const finishToolbarDrag = (event: React.PointerEvent<HTMLButtonElement>) => {
    const drag = toolbarDrag.current;
    if (drag?.pointerId !== event.pointerId) return;
    toolbarDrag.current = null;
    setToolbarLayout((layout) => ({ ...layout, floatingPosition: drag.position }));
    if (event.currentTarget.hasPointerCapture(event.pointerId)) event.currentTarget.releasePointerCapture(event.pointerId);
  };

  const beginColorSelection = (event: React.MouseEvent<HTMLDivElement> | React.PointerEvent<HTMLDivElement>) => {
    styleStart.current = { shapes, selectedId };
    colorSelectionPointer.current = (event.target as Element).closest('.saturation-white, .saturation-black')
      ? { id: (event as React.PointerEvent).pointerId, start: { x: event.clientX, y: event.clientY }, dragged: false } : null;
  };

  return <div ref={rootRef} className={styles.root} data-preserve-clip-workspace data-drawing-active={active && !playing}>
    {(!active || !toolbarVisible || (playing && !focused)) && <Tooltip {...drawingTooltipDefaults} title="Draw on video">
      <button type="button" className={styles.open} aria-label="Draw on video" data-side={launcherPosition?.side || 'right'}
        style={launcherPosition ? { top: `clamp(${launcherPosition.side === 'right' ? 42 : 8}px, ${launcherPosition.y * 100}%, calc(100% - ${launcherBottomSpace}px))` } : undefined}
        onPointerDown={(event) => {
          if (event.button !== 0) return;
          event.stopPropagation(); launcherMoved.current = false;
          launcherDrag.current = { pointerId: event.pointerId, start: { x: event.clientX, y: event.clientY } };
          event.currentTarget.setPointerCapture(event.pointerId);
        }} onPointerMove={(event) => {
          const drag = launcherDrag.current;
          const video = rootRef.current?.getBoundingClientRect();
          if (!drag || drag.pointerId !== event.pointerId || !video) return;
          if (Math.hypot(event.clientX - drag.start.x, event.clientY - drag.start.y) < 4 && !launcherMoved.current) return;
          launcherMoved.current = true;
          const side = event.clientX < video.left + video.width / 2 ? 'left' : 'right';
          setLauncherPosition({ side,
            y: Math.max(side === 'right' ? 42 : 8, Math.min(video.height - launcherBottomSpace, event.clientY - video.top - 15)) / Math.max(1, video.height) });
        }} onPointerUp={finishLauncherDrag} onPointerCancel={finishLauncherDrag} onLostPointerCapture={finishLauncherDrag}
        onClick={(event) => {
          event.stopPropagation();
          if (launcherMoved.current) return;
          if (active && !playing) setToolbarVisible(true); else beginDrawing();
        }}><DrawIcon /></button>
    </Tooltip>}
    {active && (!playing || focused) && <>
      <svg ref={canvasRef} className={styles.canvas} viewBox={`0 0 1000 ${viewHeight}`}
        style={{ display: playing ? 'none' : undefined, left: frame.x, top: frame.y, width: frame.width, height: frame.height, pointerEvents: toolbarVisible ? 'auto' : 'none', cursor: !canEditDrawing || tool === 'select' ? 'default' : tool === 'eraser' ? 'none' : 'crosshair' }}
        aria-label="Video drawing canvas" aria-readonly={!canEditDrawing}
        onPointerEnter={canEditDrawing && tool === 'eraser' ? (event) => setEraserPosition(pointFromEvent(event)) : undefined}
        onPointerLeave={() => setEraserPosition(null)}
        onPointerDown={onPointerDown} onPointerMove={canEditDrawing ? onPointerMove : undefined}
        onPointerUp={canEditDrawing ? (event) => finish(event) : undefined} onPointerCancel={canEditDrawing ? (event) => { setEraserPosition(null); finish(event, true); } : undefined}
        onLostPointerCapture={canEditDrawing ? (event) => finish(event, event.buttons !== 0) : undefined} onClick={(event) => {
          event.stopPropagation();
        }} onDoubleClick={(event) => {
          event.preventDefault(); event.stopPropagation();
          const clicked = clickedShape.current;
          if (!canEditDrawing || !clicked?.id) return;
          // Undo drawing/erasing caused by the two clicks before selecting.
          setShapes(clicked.shapes); setUndoHistory(clicked.undo); setSelectedId(clicked.id); setTool('select');
          setDraft(null); anglePoints.current = []; gesture.current = null;
          if (clicked.shapes.find((shape) => shape.id === clicked.id)?.tool === 'text') {
            textEditStart.current = { shapes: clicked.shapes, selectedId: clicked.id }; setEditingTextId(clicked.id);
          }
        }}>
        {shapes.filter((shape) => shape.id !== editingTextId).map((shape) => <DrawingShape key={shape.id} shape={shape} />)}
        {draft && <DrawingShape shape={draft} />}
        {toolbarVisible && canEditDrawing && tool === 'eraser' && eraserPosition && <circle
          cx={eraserPosition.x} cy={eraserPosition.y} r={eraserWidth / 2} fill="#000" stroke="#fff" strokeWidth={1}
          vectorEffect="non-scaling-stroke" pointerEvents="none" aria-hidden="true" style={{ filter: 'drop-shadow(0 0 1px #000)' }} />}
        {toolbarVisible && canEditDrawing && selected && (tool === 'select' ||
          ((tool === 'line' || tool === 'arrow' || tool === 'trapezoid') && selected.tool === tool)) && <g data-drawing-id={selected.id} className={styles.selection}>
          <rect {...bounds(selected)} fill="none" stroke="#fff" strokeWidth={1.5} strokeDasharray="5 5" pointerEvents={selected.tool === 'text' ? 'stroke' : 'none'} />
          {resizeHandles(selected).map((p, index) =>
            <circle key={index} cx={p.x} cy={p.y} r={5 * 1000 / Math.max(1, frame.width)} data-handle={index}
              fill="#0d2637" stroke="#35b9ee" strokeWidth={2}
              style={selected.tool === 'text' ? { cursor: index >= 6 ? 'ns-resize' : index >= 4 ? 'ew-resize' : index % 2 ? 'nesw-resize' : 'nwse-resize' } : undefined}>
              {selected.tool === 'text' && <title>{index >= 6 ? 'Resize text height' : index >= 4 ? 'Resize text width' : 'Scale text'}</title>}
              {(selected.tool === 'line' || selected.tool === 'arrow') && index === 2 && <title>Drag to curve</title>}
            </circle>)}
        </g>}
        {toolbarVisible && canEditDrawing && selected?.id === editingTextId && selected?.tool === 'text' && (() => {
          const box = bounds(selected);
          const layout = textLayout(selected);
          return <foreignObject x={box.x} y={box.y} width={box.width} height={box.height}>
            <textarea ref={textEditorRef} className={styles.inlineText} aria-label="Drawing text" placeholder="Type text…"
              value={selected.text} style={{ color: selected.color, opacity: selected.opacity, fontSize: layout.size,
                fontFamily: selected.fontFamily || 'Arial', textAlign: selected.textAlign || 'left',
                background: selected.fill ? 'rgba(80,80,80,.55)' : 'transparent', whiteSpace: selected.wrap === false ? 'pre' : 'pre-wrap' }}
              onPointerDown={(event) => event.stopPropagation()} onClick={(event) => event.stopPropagation()}
              onDoubleClick={(event) => event.stopPropagation()} onBlur={finishTextEdit}
              onKeyDown={(event) => {
                event.stopPropagation();
                if ((event.key === 'Enter' && !event.shiftKey) || event.key === 'Escape') { event.preventDefault(); event.currentTarget.blur(); }
              }} onChange={(event) => setShapes((previous) => previous.map((shape) => shape.id === selectedId ? { ...shape, text: event.target.value } : shape))} />
          </foreignObject>;
        })()}
      </svg>
      {toolbarVisible && createPortal(<>
        {focused && focusShades.map((rect, index) => <div key={index} className={styles.focusShade} aria-hidden="true"
          style={{ left: rect.left, top: rect.top, width: rect.width, height: rect.height }} />)}
        <div ref={toolbarRef} className={styles.toolbar} role="toolbar" aria-label="Drawing tools"
        data-preserve-clip-workspace data-preserve-clip-edit data-dock={toolbarDock} data-focus={focused} data-tall={toolbarTall} data-orientation={toolbarVertical ? 'vertical' : 'horizontal'}
        style={toolbarPosition ? { left: toolbarPosition.x, top: toolbarPosition.y } : { visibility: 'hidden' }}
        onPointerDown={(event) => event.stopPropagation()} onMouseDown={(event) => event.stopPropagation()}
        onClick={(event) => event.stopPropagation()}>
        {toolbarDock === 'floating' && !focused && <button type="button" className={styles.toolbarGrip} aria-label="Move floating toolbar"
          onPointerDown={(event) => {
            if (event.button !== 0) return;
            event.preventDefault(); event.stopPropagation();
            const rect = toolbarRef.current!.getBoundingClientRect();
            const position = { x: rect.left, y: rect.top };
            toolbarDrag.current = { pointerId: event.pointerId, start: { x: event.clientX, y: event.clientY }, origin: position, position };
            event.currentTarget.setPointerCapture(event.pointerId);
          }} onPointerMove={(event) => {
            const drag = toolbarDrag.current;
            if (!drag || drag.pointerId !== event.pointerId) return;
            drag.position = clampToolbarPosition({ x: drag.origin.x + event.clientX - drag.start.x, y: drag.origin.y + event.clientY - drag.start.y });
            setToolbarPosition(drag.position);
          }} onPointerUp={finishToolbarDrag} onPointerCancel={finishToolbarDrag} onLostPointerCapture={finishToolbarDrag}>
          <svg viewBox="0 0 24 12" aria-hidden="true"><path d="M4 4h16M4 8h16" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" /></svg>
        </button>}
        <div className={styles.toolbarHeader}>
        {focused && <span className={styles.panelTitle}>Tools</span>}
        {!focused && <Tooltip {...drawingTooltipProps} title="Choose where to dock the toolbar">
          <button type="button" aria-label="Pin drawing toolbar" aria-expanded={!!dockAnchor} aria-pressed={toolbarDock !== 'floating'}
            onClick={(event) => setDockAnchor(dockAnchor ? null : event.currentTarget)}><PinIcon /><span className={styles.buttonCaption}>Pin</span></button>
        </Tooltip>}
        <Tooltip {...drawingTooltipProps} title={focused ? 'Return to compact drawing tools (Esc)' : 'Switch to detailed drawing tools'}>
          <button type="button" className={styles.detailButton} aria-label="Detailed view" aria-pressed={focused}
            onClick={() => setToolbarLayout((layout) => ({ ...layout, focusMode: !focused }))}><FocusIcon /><span className={styles.buttonCaption}>Detailed</span></button>
        </Tooltip>
        {toolbarDock === 'floating' && !focused && <Tooltip {...drawingTooltipProps} title={toolbarVertical ? 'Switch to horizontal float' : 'Switch to vertical float'}>
        <button type="button" className={styles.orientationButton} aria-label={toolbarVertical ? 'Switch to horizontal float' : 'Switch to vertical float'}
          onClick={() => setToolbarLayout((layout) => ({ ...layout, floatingDock: toolbarVertical ? 'top' : 'right' }))}>
          <svg viewBox="0 0 24 24" aria-hidden="true"><path d="M4 8a8 8 0 0 1 13-3l3 3M20 3v5h-5M20 16a8 8 0 0 1-13 3l-3-3M4 21v-5h5" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" /></svg>
        </button></Tooltip>}
        {focused && <button type="button" className={styles.closeButton} aria-label="Close drawing tools (left panel)" onClick={closeToolbar}><CloseIcon /></button>}
        {!focused && <Tooltip {...drawingTooltipProps} title="Close the drawing toolbar">
          <button type="button" className={styles.closeButton} aria-label="Close drawing tools" onClick={closeToolbar}><CloseIcon /><span className={styles.buttonCaption}>Close</span></button>
        </Tooltip>}
        </div>
        {focused && <div className={styles.propertiesHeader}><span className={styles.panelTitle}>Style</span>
          <Tooltip {...drawingTooltipProps} title="Return to compact drawing tools (Esc)"><button type="button" aria-label="Exit focus mode" aria-pressed={focused}
            onClick={() => setToolbarLayout((layout) => ({ ...layout, focusMode: false }))}><FocusIcon /></button></Tooltip>
          <Tooltip {...drawingTooltipProps} title="Close the drawing toolbar"><button type="button" className={styles.closeButton} aria-label="Close drawing tools" onClick={closeToolbar}><CloseIcon /></button></Tooltip>
        </div>}
        <Popper open={!!dockAnchor} anchorEl={dockAnchor} container={toolbarContainer}
          placement={toolbarDock === 'right' ? 'left-start' : toolbarDock === 'left' ? 'right-start' : 'bottom-start'}
          className={styles.popover} modifiers={[{ name: 'offset', options: { offset: [0, 8] } }]} data-preserve-clip-workspace data-preserve-clip-edit>
          <ClickAwayListener onClickAway={() => setDockAnchor(null)}>
            <div className={styles.dockOptions} aria-label="Toolbar position">
              {(['floating', 'top', 'bottom', 'left', 'right'] as ToolbarDock[]).map((edge) =>
                <button key={edge} type="button" aria-label={edge === 'floating' ? 'Float drawing toolbar' : `Pin toolbar to ${edge} edge`}
                  aria-pressed={toolbarDock === edge} onClick={() => {
                    setToolbarLayout((layout) => ({ ...layout, dock: edge, focusMode: false,
                      floatingDock: edge === 'floating' && layout.dock !== 'floating' ? layout.dock : layout.floatingDock }));
                    setDockAnchor(null);
                  }}>
                  <span className={styles.dockPreview} data-edge={edge} /><span>{edge === 'floating' ? 'Float' : edge}</span>
                </button>)}
            </div>
          </ClickAwayListener>
        </Popper>
        <div className={styles.tools}>
          <div className={styles.drawingTools}>
          {canEditDrawing ? <>
          {tools.map(({ tool: value, label, Icon }) => <Tooltip {...drawingTooltipProps} title={toolHints[value] || label} key={value}>
            <button type="button" className={styles.expandButton} aria-label={label} aria-pressed={tool === value}
              onClick={() => chooseTool(value)}><Icon /><span className={styles.buttonCaption}>{value === 'pen' ? 'Pen' : value === 'beam' ? 'Beam' : value === 'square' ? 'Rect' : toolCaptions[value]}</span></button>
          </Tooltip>)}
          </> : <><span className={styles.readOnly}>Saved drawing · View only</span><button type="button" className={styles.startOwn} onClick={beginDrawing}>Start own drawing</button></>}
          </div>
          <div className={styles.actions}>
          {canEditDrawing && <>
          {iconButton('Undo last drawing action', UndoIcon, undo, !undoHistory.length || !!draft, 'Undo',
            draft ? 'Finish the current drawing before undoing' : undoHistory.length ? 'Undo the last drawing change' : 'Nothing to undo')}
          {iconButton('Delete selected drawing', DeleteIcon, deleteSelected, !selected, 'Delete',
            selected ? 'Delete the selected drawing' : 'Select a drawing to delete')}
          {iconButton('Clear drawings', ClearIcon, () => {
            const before = { shapes, selectedId }; const aspect = drawingAspect;
            clear(); setUndoHistory([...undoHistory, before]); setDrawingAspect(savedMarker?.drawing?.aspect || aspect);
          }, !shapes.length && !draft, 'Clear', shapes.length || draft ? 'Remove all drawings from this frame' : 'No drawings to clear')}
          <span className={styles.actionBreak} />
          </>}
          {iconButton('Open drawing comments', CommentIcon, () => (focused ? toggleFocusDiscussion : openDiscussion)(savedMarker || undefined, savedMarker ? drawingTarget?.clipId : selectedClip?.id), false, 'Comments',
            savedMarker ? 'Jump to comments for this drawing' : 'Open comments for the current clip or video')}
          {!focused && saveAction}
          </div>
        </div>
        {(canEditDrawing || focused) && <div className={styles.settings}
          onPointerDownCapture={() => { styleStart.current = { shapes, selectedId }; }}
          onPointerUpCapture={() => { styleStart.current = null; }} onPointerCancelCapture={() => { styleStart.current = null; }}>
          <div className={styles.colorControl} data-setting-hidden={!focused && styleTool === 'eraser'}>
            <Tooltip {...drawingTooltipProps} title={colorPickerOpen ? '' : selected ? `Change selected ${toolCaptions[selected.tool].toLowerCase()} color` : 'Choose color for new drawings'}>
              <button type="button" ref={colorButtonRef} className={styles.colorButton} aria-label="Drawing color" aria-haspopup="dialog" aria-expanded={colorPickerOpen} disabled={!canEditDrawing || styleTool === 'eraser'}
                onClick={() => setColorPickerOpen((open) => !open)}>
                <span style={{ backgroundColor: selected?.color || color }} /><span className={styles.buttonCaption}>Color</span>
              </button>
            </Tooltip>
          </div>
          <Tooltip {...drawingTooltipProps} title="Change line thickness">
          <label data-setting-hidden={!focused && styleTool === 'beam'} data-disabled={!canEditDrawing || !textOptionsDisabled || styleTool === 'beam'}><span className={styles.settingText}>{tool === 'eraser' ? 'Eraser size' : 'Width'}</span><output className={styles.settingValue}>{currentWidth} px</output><ThicknessIcon className={styles.settingIcon} /><input type="range" aria-label="Drawing thickness" disabled={!canEditDrawing || !textOptionsDisabled || styleTool === 'beam'} min="2" max="16" value={currentWidth}
            onChange={(event) => {
              if (tool === 'eraser') setEraserWidth(+event.target.value);
              else { setWidth(+event.target.value); changeStyle({ width: +event.target.value }); }
            }} /></label>
          </Tooltip>
          <Tooltip {...drawingTooltipProps} title="Change drawing opacity">
          <label data-setting-hidden={!focused && styleTool === 'eraser'} data-disabled={!canEditDrawing || styleTool === 'eraser'}><span className={styles.settingText}>Opacity</span><output className={styles.settingValue}>{Math.round((selected?.opacity ?? opacity) * 100)}%</output><OpacityIcon className={styles.settingIcon} /><input type="range" aria-label="Drawing opacity" disabled={!canEditDrawing || styleTool === 'eraser'} min="0.1" max="1" step="0.1" value={selected?.opacity ?? opacity}
            onChange={(event) => { setOpacity(+event.target.value); changeStyle({ opacity: +event.target.value }); }} /></label>
          </Tooltip>
          <Tooltip {...drawingTooltipProps} title="Fill shapes or add a text background">
          <button type="button" data-setting-hidden={!focused && !supportsFill} aria-label="Fill" disabled={!canEditDrawing || !supportsFill} aria-pressed={selected?.fill ?? fill} onClick={() => {
            const value = !(selected?.fill ?? fill); setFill(value); changeStyle({ fill: value });
          }}><span className={styles.settingText}>Fill</span><FillIcon className={styles.settingIcon} /></button>
          </Tooltip>
          <Tooltip {...drawingTooltipProps} title="Use dashed lines">
          <button type="button" data-setting-hidden={!focused && !supportsDashed} aria-label="Dashed" disabled={!canEditDrawing || !supportsDashed} aria-pressed={selected?.dashed ?? dashed} onClick={() => {
            const value = !(selected?.dashed ?? dashed); setDashed(value); changeStyle({ dashed: value });
          }}><span className={styles.settingText}>Dashed</span><DashedIcon className={styles.settingIcon} /></button>
          </Tooltip>
          <button type="button" className={styles.fontOptionsButton} aria-label="Text formatting" disabled={!canEditDrawing || textOptionsDisabled}
            aria-expanded={!!fontAnchor} onClick={(event) => setFontAnchor(fontAnchor ? null : event.currentTarget)}>Aa</button>
        </div>}
        {focused && saveAction}
      </div>
      <Popper open={!!fontAnchor} anchorEl={fontAnchor} container={toolbarContainer} className={styles.popover}
        placement="bottom-end" popperOptions={{ strategy: 'fixed' }}
        modifiers={[{ name: 'offset', options: { offset: [0, 8] } }, { name: 'preventOverflow', options: { padding: 8, altAxis: true } }]}
        data-preserve-clip-workspace data-preserve-clip-edit>
        <ClickAwayListener onClickAway={() => setFontAnchor(null)}>
          <div onPointerDown={(event) => { event.stopPropagation(); styleStart.current = { shapes, selectedId }; }}
            onPointerUp={() => { styleStart.current = null; }} onKeyDown={(event) => {
              if (event.key === 'Escape') { event.stopPropagation(); setFontAnchor(null); }
            }}>
          <div role="toolbar" aria-label="Text formatting tools" className={styles.fontOptions}>
            <label data-disabled={textOptionsDisabled}>Font<select disabled={textOptionsDisabled} aria-label="Text font" value={selected?.fontFamily || fontFamily} onChange={(event) => { setFontFamily(event.target.value); changeStyle({ fontFamily: event.target.value }); }}>
              {['Arial', 'Georgia', 'Verdana', 'Courier New'].map((font) => <option key={font}>{font}</option>)}
            </select></label>
            <label data-disabled={textOptionsDisabled}>Size<select disabled={textOptionsDisabled} aria-label="Text size" value={selected?.fontSize || (selected ? 24 + selected.width * 2 : fontSize)} onChange={(event) => { setFontSize(+event.target.value); changeStyle({ fontSize: +event.target.value }); }}>
              {Array.from(new Set([16, 24, 28, 32, 36, 40, 48, 56, 64, 80, 96, selected?.fontSize || (selected ? 24 + selected.width * 2 : fontSize)])).sort((a, b) => a - b).map((size) => <option key={size}>{size}</option>)}
            </select></label>
            <label data-disabled={textOptionsDisabled}>Align<select disabled={textOptionsDisabled} aria-label="Text alignment" value={selected?.textAlign || textAlign} onChange={(event) => {
              const value = event.target.value as 'left' | 'center' | 'right'; setTextAlign(value); changeStyle({ textAlign: value });
            }}>{['left', 'center', 'right'].map((alignment) => <option key={alignment}>{alignment}</option>)}</select></label>
            <Tooltip {...drawingTooltipProps} title="Automatically wrap lines to fit the text box">
              <button type="button" className={styles.wrapControl} aria-label="Wrap text" disabled={textOptionsDisabled}
                aria-pressed={selected?.wrap ?? wrap}
                onKeyDown={(event) => { if (event.key === ' ') event.stopPropagation(); }} onClick={() => {
                  const value = !(selected?.wrap ?? wrap); setWrap(value); changeStyle({ wrap: value });
                }}><span className={styles.wrapCopy}><span>Wrap Text</span>
                  <span className={styles.wrapHint}>{(selected?.wrap ?? wrap) ? 'Fit to text box' : 'Manual breaks only'}</span>
                </span><WrapIcon /></button>
            </Tooltip>
          </div>
          </div>
        </ClickAwayListener>
      </Popper>
      <Popper open={colorPickerOpen} anchorEl={focused ? colorButtonRef.current : toolbarVertical ? toolbarRef.current : colorButtonRef.current} container={toolbarContainer}
        placement={focused ? 'left-start' : toolbarVertical ? toolbarDock === 'right' ? 'left-start' : 'right-start' : 'bottom-start'}
        popperOptions={{ strategy: 'fixed' }} style={{ zIndex: 1500 }} modifiers={[
          { name: 'offset', options: { offset: [0, 12] } },
          { name: 'flip', options: { fallbackPlacements: toolbarVertical ? ['left-start', 'right-start'] : ['top-start'] } },
          { name: 'preventOverflow', options: { padding: 12, altAxis: true } },
        ]}>
        <div ref={colorWindowRef} className={styles.colorWindow} role="dialog" aria-labelledby="drawing-color-title" aria-describedby="drawing-color-target"
          data-preserve-clip-workspace data-preserve-clip-edit
          onPointerDownCapture={beginColorSelection} onMouseDownCapture={beginColorSelection}
          onPointerUpCapture={() => { styleStart.current = null; }} onPointerCancelCapture={() => { styleStart.current = null; }}
          onPointerDown={(event) => event.stopPropagation()} onKeyDown={(event) => {
            if (event.key === 'Enter' && event.target instanceof HTMLInputElement) { event.preventDefault(); setColorPickerOpen(false); }
          }} onBlur={(event) => {
            if (event.target instanceof HTMLInputElement && !event.currentTarget.contains(event.relatedTarget as Node)) setColorPickerOpen(false);
          }} onMouseDown={(event) => event.stopPropagation()} onClick={(event) => event.stopPropagation()}>
          <h3 id="drawing-color-title">Drawing color</h3>
          <p id="drawing-color-target">{selected
            ? `Applies to the selected ${toolCaptions[selected.tool].toLowerCase()} only.`
            : 'Applies to new drawings. Existing drawings stay unchanged.'}</p>
          <ChromePicker disableAlpha color={selected?.color || color}
            styles={{ default: { picker: { width: '100%', background: '#102737', boxShadow: 'none', borderRadius: '8px' } } }}
            onChange={({ hex }) => {
              if (selected) changeStyle({ color: hex }); else setColor(hex);
            }} />
          <button type="button" className={styles.colorClose} onClick={() => setColorPickerOpen(false)}><CloseIcon />Close</button>
        </div>
      </Popper></>, toolbarContainer)}
    </>}
  </div>;
};
